import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileBeforeUploadEvent, FileUploadEvent } from 'primeng/fileupload';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { SelectableDocument } from './file-upload.service';
import { ExtractionConfig } from '@shared/index';
import { DocumentChunk } from '@reflact/docsearch';

@Injectable({
  providedIn: 'root'
})
export class ProjectDocumentsService {
  private _selectedProjectId: string = "";
  public uploadEndpoint = "/api/toolbox/bot/admin/botdoc/upload";
  public replaceEndpoint = "/api/toolbox/bot/admin/botdoc/replace";

  public projectDocuments: BehaviorSubject<SelectableDocument[]> = new BehaviorSubject<SelectableDocument[]>([]);


  constructor(public http: HttpClient) {

  }
  public addFormFieldsForReplace(event: FileBeforeUploadEvent, docId: string) {
    event.formData.append('docid', docId);
  }
  public onReplaceDone(_event: FileUploadEvent) {
    void this.updateProjectDocuments();
  }



  public addFormFieldsForUpload(event: FileBeforeUploadEvent) {

    event.formData.append('projectId', this._selectedProjectId);
  }
  public fileUploadDone(_event: FileUploadEvent) {
    void this.updateProjectDocuments();
  }


  private async updateProjectDocuments() {
    const result = await firstValueFrom(this.http.get<SelectableDocument[]>("/api/toolbox/bot/admin/projectdoc/files/" + this._selectedProjectId));
    this.projectDocuments.next(result);
  }

  public async reloadDocuments() {
    await this.updateProjectDocuments();
  }


  public async deleteFile(fileId: string) {
    try {
      const res = await firstValueFrom(this.http.delete(`/api/toolbox/bot/admin/botdoc/delete/${this.selectedProjectId}/${fileId}`));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while deleting file', error);
      return false;
    }
  }

  public async setDocumentAutoUpdate(fileId: string, autoUpdateEnabled: boolean) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/autoupdate/${this.selectedProjectId}/${fileId}`,
        { autoUpdateEnabled }
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while updating auto update flag', error);
      return false;
    }
  }

  public async reloadWebsiteDocument(fileId: string) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/${this.selectedProjectId}/${fileId}/website-reload`,
        {}
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while reloading website document', error);
      return false;
    }
  }

  public async updateWebsiteAutoUpdateConfig(
    fileId: string,
    autoUpdateEnabled: boolean,
    autoUpdateIntervalDays?: number,
    urls?: string[]
  ) {
    try {
      const res = await firstValueFrom(this.http.post(
        `/api/toolbox/bot/admin/projectdoc/${this.selectedProjectId}/${fileId}/website-update-config`,
        { autoUpdateEnabled, autoUpdateIntervalDays, urls }
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error) {
      console.error('Error while updating website auto-update config', error);
      return false;
    }
  }

  public async triggerExtraction(docId: string, config: ExtractionConfig): Promise<{ status: string, chunkCount?: number, message?: string, errorType?: string } | null> {
    try {
      const res = await firstValueFrom(this.http.post<{ status: string, chunkCount?: number, message?: string, errorType?: string }>(
        `/api/toolbox/bot/admin/botdoc/extract/${docId}`,
        config
      ));
      void this.updateProjectDocuments();
      return res;
    } catch (error: unknown) {
      console.error('Error while triggering extraction', error);
      const e = error as { error?: { message?: string; errorType?: string } };
      const message: string | undefined = e.error?.message ?? (error as { message?: string }).message;
      const errorType: string | undefined = e.error?.errorType;
      return { status: "error", message, errorType };
    }
  }

  public set selectedProjectId(id: string) {
    if (id != this._selectedProjectId) {
      this._selectedProjectId = id;
      this.projectDocuments.next([]);
      void this.updateProjectDocuments();
    }

  }

  public get selectedProjectId() {
    return this._selectedProjectId;
  }

  public async getProjectDocChunks(docSearchId: string): Promise<DocumentChunk[]> {
    return firstValueFrom(this.http.get<DocumentChunk[]>(`/api/toolbox/bot/admin/projectdoc/chunks/${docSearchId}`));
  }
}
